import { Dialog } from '@ariakit/react';
import type { DialogProps } from '@ariakit/react';
import type { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormikContext } from 'formik';
import type { CSSProperties, FC, MouseEventHandler } from 'react';
import { useEffect, useState } from 'react';
import { ActionModeProvider } from 'src/components/action-mode';
import { ActionGroupModeProvider } from 'src/components/buttons-and-actions/action-group';
import type { ButtonProps } from 'src/components/buttons-and-actions/button';
import { Button } from 'src/components/buttons-and-actions/button';
import { XIcon } from 'src/components/media-and-icons/icons';
import { Tooltip } from 'src/components/overlay/tooltip';
import { Heading } from 'src/components/text/heading';
import { useMeasure } from 'src/hooks/use-measure';
import { sonnerContainerId } from 'src/hooks/use-sonner';
import { useStorybook } from 'src/hooks/use-storybook';
import { useIsNewDesignActive } from 'src/pages/new-design/is-new-design';
import { cn } from 'src/utils/cn';
import type { StrictOmit } from 'src/utils/utility-types';

type Size = 'default' | 'sm' | 'md' | 'lg';

const sizeWidths: Record<Size, string> = {
  default: '600px',
  sm: '300px',
  md: '450px',
  lg: '900px',
};

type SubmitButtonProps = {
  label: string;
  dataTestId?: string;
  disabled?: boolean;
  props?: ButtonProps;
};

type CloseButtonProps = {
  label?: string;
  dataTestId?: string;
  disabled?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  props?: ButtonProps;
};

type ConfirmButtonProps = {
  onClick: MouseEventHandler<HTMLButtonElement>;
  label: string;
  dataTestId?: string;
  disabled?: boolean;
  props?: ButtonProps;
  tooltip?: string | boolean;
};

type Footer =
  | {
      submitButton: SubmitButtonProps;
      closeButton?: CloseButtonProps | null;
      confirmButton?: never;
    }
  | {
      confirmButton: ConfirmButtonProps | null;
      closeButton?: CloseButtonProps | null;
      submitButton?: never;
    };

type Props = {
  icon?: IconDefinition | undefined;
  title: string;
  size?: Size;
  /**
   * Be very careful with this one. Usually there is no good reason why you should
   * NOT be able to close a modal. At the end you cannot force people to do something
   * within the modal or keep it open for some time. They could simply refresh or navigate
   * away from the page.
   */
  hideClose?: boolean;
  /**
   * You can use the default footer by just passing in the confirmationButton specification.
   * * By default, closeButton is a button that closes the modal when clicked.
   * * If you set closeButton to null explicitly, you will have no close button.
   * * If your modal has a form, use submitButton instead of confirmButton. */
  footer: Footer | null;
  onDismiss?: () => void;
} & StrictOmit<DialogProps, 'open' | 'backdrop' | 'onClose' | 'style'>;

export const Modal: FC<Props> = ({
  icon,
  title,
  size = 'default',
  hideClose,
  footer,
  onDismiss,
  children,
  ...dialogProps
}) => {
  const isNewDesignActive = useIsNewDesignActive();
  const { isStorybook } = useStorybook();

  useEffect(() => {
    // Layout fix for ariakit dialog
    // see https://github.com/ariakit/ariakit/issues/4345#issuecomment-2579469808
    document.documentElement.classList.add(
      'overflow-x-visible!',
      '[&_body]:pr-0!'
    );
    return () => {
      document.documentElement.classList.remove(
        'overflow-x-visible!',
        '[&_body]:pr-0!'
      );
    };
  }, []);

  const [isScrollable, setIsScrollable] = useState(false);
  const [innerContentRef, innerContentBounds] = useMeasure();

  useEffect(() => {
    if (innerContentBounds.height > 300) {
      setIsScrollable(true);
    } else {
      setIsScrollable(false);
    }
  }, [innerContentBounds]);

  return (
    // reset ActionModeProvider and ActionGroupModeProvider in case
    // a modal gets nested inside them
    <ActionModeProvider value={null}>
      <ActionGroupModeProvider value="single">
        <Dialog
          {...dialogProps}
          open
          // Allow toasts to be interactive, even when rendered outside the modal
          getPersistentElements={() =>
            [document.getElementById(sonnerContainerId)].filter(
              (element) => element !== null
            )
          }
          backdrop={
            <div
              className={cn(
                `
                  bg-background-surface-inverted-default/54
                  legacy:bg-[#00000054]
                `,
                !isStorybook && `animate-fade-in-fast`
              )}
            />
          }
          onClose={onDismiss}
          className={cn(
            `
              fixed inset-0 mx-auto my-[10vh] h-fit w-(--modal-size)
              max-w-[calc(100%-var(--spacing-xl)*2)] rounded-sm
              shadow-elevation-md
            `,
            `
              legacy:max-w-[calc(100%-1.5rem*2)] legacy:rounded-[0.6rem]
              legacy:shadow-[0_0.5rem_1.5rem_rgba(0,0,0,0.5)]
            `,
            !isStorybook && `animate-fade-in-fast`
          )}
          style={
            {
              '--modal-size': sizeWidths[size],
            } as CSSProperties
          }
        >
          {title && (
            <div
              className={cn(
                `
                  grid grid-flow-col grid-cols-[auto_min-content] items-center
                  gap-xl rounded-t-sm bg-background-surface-inverted-default
                  px-xl py-lg text-headline-md break-all
                  text-foreground-surface-inverted-default
                `,
                icon && `grid-cols-[auto_1fr]`,
                `
                  legacy:gap-[1.5rem] legacy:bg-legacy-brand legacy:p-[1.5rem]
                  legacy:text-[2rem] legacy:text-white
                `
              )}
            >
              {icon && <FontAwesomeIcon icon={icon} />}

              {isNewDesignActive ? (
                <Heading
                  mode="title"
                  size="md"
                  className={cn(`
                    not-legacy:text-foreground-surface-inverted-default
                  `)}
                >
                  {title}
                </Heading>
              ) : (
                <p
                  className={cn(`text-[2rem] leading-[2.85714rem] font-[900]`)}
                >
                  {title}
                </p>
              )}

              {!hideClose && (
                <Button
                  mode="icon-inverse"
                  onClick={onDismiss}
                  className={cn(
                    `
                      not-legacy:text-foreground-surface-inverted-subdued
                      not-legacy:hover:text-foreground-surface-inverted-hover
                      not-legacy:active:text-foreground-surface-inverted-active
                    `,
                    `
                      legacy:self-start legacy:text-[2.2rem]
                      legacy:leading-[2.53rem] legacy:text-legacy-brand-light-1
                      legacy:not-disabled:not-active:hover:text-legacy-brand-light-2
                      legacy:focus:text-legacy-brand-light-1!
                    `
                  )}
                >
                  {isNewDesignActive ? (
                    <XIcon size="md" />
                  ) : (
                    <FontAwesomeIcon icon={faTimes} aria-label="Close modal" />
                  )}
                </Button>
              )}
            </div>
          )}

          <div
            className={cn(
              `bg-background-surface-default p-xl`,
              `legacy:bg-white legacy:p-[1.5rem]`,
              isScrollable && `max-h-[50vh] overflow-y-auto`,
              !footer && `rounded-b-none`
            )}
          >
            {/* we need this div to measure the children's height and set the parent div to scrollable */}
            <div ref={innerContentRef}>{children}</div>
          </div>

          {footer && (
            <div
              className={cn(
                `
                  grid grid-flow-col items-center gap-xl rounded-b-sm
                  bg-background-surface-default p-xl
                `,
                `
                  legacy:gap-[2rem] legacy:rounded-b-[0.6rem] legacy:bg-white
                  legacy:p-[1.5rem]
                `,
                footer.closeButton === null ? `justify-end` : `justify-between`
              )}
            >
              {footer.closeButton === undefined ? (
                <Button
                  mode="secondary"
                  data-testid="close-modal"
                  onClick={onDismiss}
                >
                  Close
                </Button>
              ) : footer.closeButton ? (
                <Button
                  mode="secondary"
                  data-testid={footer.closeButton.dataTestId}
                  onClick={footer.closeButton.onClick ?? onDismiss}
                  disabled={footer.confirmButton?.disabled}
                  {...footer.closeButton.props}
                >
                  {footer.closeButton.label ?? 'Close'}
                </Button>
              ) : null}
              {footer.submitButton && (
                <SubmitButton submitButton={footer.submitButton} />
              )}
              {footer.confirmButton && (
                <ConfirmButton confirmButton={footer.confirmButton} />
              )}
            </div>
          )}
        </Dialog>
      </ActionGroupModeProvider>
    </ActionModeProvider>
  );
};

const SubmitButton: FC<{ submitButton: SubmitButtonProps }> = ({
  submitButton,
}) => {
  const { submitForm } = useFormikContext();

  return (
    <Button
      disabled={submitButton.disabled}
      type="submit"
      data-testid={submitButton.dataTestId ?? 'submit-form'}
      mode="primary"
      onClick={() => submitForm()}
      {...submitButton.props}
    >
      {submitButton.label}
    </Button>
  );
};

const ConfirmButton: FC<{ confirmButton: ConfirmButtonProps }> = ({
  confirmButton,
}) => (
  <Tooltip content={confirmButton.tooltip}>
    {(targetProps) => (
      <Button
        disabled={confirmButton.disabled}
        data-testid={confirmButton.dataTestId ?? 'submit-form'}
        mode="primary"
        onClick={confirmButton.onClick}
        {...confirmButton.props}
        {...targetProps}
      >
        {confirmButton.label}
      </Button>
    )}
  </Tooltip>
);
